<script setup lang="ts">
import { onMounted, ref } from 'vue';
import Error from '../../js/model/Error.js';
import KgrCheckbox from '../../components/common/form/checkbox/Checkbox.vue';
import KgrFormRow from '../../components/common/form/FormRow.vue';
import KgrFormScaffold from '../../components/common/form/FormScaffold.vue';
import KgrLoading from '../../components/common/Loading.vue';
import Login from '../../js/dto/Login.js';
import MemberRepository from '../../js/repository/MemberRepository';
import { useDialog } from '@/plugins/dialog';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = defineProps<{
    overviewUrl: string
    memberId: number
    isDeletable: boolean
    isEditable: boolean
}>();

const { dialog } = useDialog();
const { notify } = useNotifications();
const { i18n } = useI18n();
const loading = ref(true);
const pending = ref(false);
const pendingRemove = ref(false);
const error = ref(new Error());
const memberLogin = ref<Login>(new Login());

onMounted(() => {
    load(true);
});

function load(initial = false) {
    if (initial) {
        loading.value = true;
    } else {
        pending.value = true;
    }
    MemberRepository.getLoginData(props.memberId)
        .then((response: Login) => {
            memberLogin.value = response;
        })
        .catch(() => {
            notify.error('error.loadingData');
        })
        .finally(() => {
            loading.value = false;
            pending.value = false;
        });
}

function redirectToOverview() {
    window.location.href = props.overviewUrl;
}

function onUsernameChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const updatedLogin = Login.fromData({
        username: target.value.trim(),
        enabled: memberLogin.value.enabled,
    });
    if (updatedLogin) {
        memberLogin.value = updatedLogin;
    }
}

function onEnabledChange(value: boolean) {
    const updatedLogin = Login.fromData({
        username: memberLogin.value.username,
        enabled: value,
    });
    if (updatedLogin) {
        memberLogin.value = updatedLogin;
    }
}

function updateMemberLogin() {
    pending.value = true;
    MemberRepository.updateLoginData(props.memberId, memberLogin.value)
        .then(() => {
            error.value = new Error();
            notify.success('saveChanges.success');
            return load();
        })
        .catch((err) => {
            error.value = err;
            notify.error('saveChanges.error');
        })
        .finally(() => {
            pending.value = false;
        });
}

async function removeMemberLogin() {
    if (memberLogin.value.username === '') {
        return;
    }

    if (!dialog) {
        return;
    }

    const confirmed = await dialog.confirm({
        title: i18n.t('Login löschen'),
        message: i18n.t('Soll der Login für die E-Mail Adresse {username} wirklich gelöscht werden?', { username: memberLogin.value.username }),
        confirmButtonLabel: i18n.t('Löschen'),
    });

    if (!confirmed) {
        return;
    }

    pendingRemove.value = true;
    MemberRepository.removeLoginData(props.memberId)
        .then(() => {
            error.value = new Error();
            notify.success('Login erfolgreich gelöscht');
            return load();
        })
        .catch((err) => {
            error.value = err;
            notify.error('saveChanges.error');
        })
        .finally(() => {
            pendingRemove.value = false;
        });
}
</script>

<template>
    <kgr-form-scaffold
        :pending="pending || loading"
        :pending-delete="pendingRemove"
        :with-delete-button="isDeletable"
        edit-mode
        :with-primary-buttons="isEditable"
        @primary-btn-clicked="updateMemberLogin"
        @delete-btn-clicked="removeMemberLogin"
        @back-btn-clicked="redirectToOverview"
    >
        <kgr-loading v-if="loading" />
        <div
            v-else
            class="animated fadeIn"
        >
            <div
                class="kgr-alert kgr-alert--info kgr-row"
                v-html="i18n.t('member.login.info')"
            ></div>

            <kgr-form-row
                :label="i18n.t('E-Mail')"
                :error="error"
                :required="true"
                field-name="username"
            >
                <input
                    v-model="memberLogin.username"
                    type="email"
                    class="kgr-input"
                    @input="onUsernameChange"
                />
            </kgr-form-row>

            <kgr-form-row
                :label="i18n.t('Login aktiviert')"
                :error="error"
                field-name="enabled"
            >
                <kgr-checkbox
                    :model-value="memberLogin.enabled"
                    :label="i18n.t('Login für dieses Mitglied aktivieren')"
                    @update:model-value="onEnabledChange"
                />
            </kgr-form-row>
        </div>
    </kgr-form-scaffold>
</template>

<style scoped lang="scss" rel="stylesheet/scss"></style>
